/**
 * Static content and types for the "Establish the Center and Occupancy" workflow.
 *
 * Language canon for this whole folder: the designation vocabulary is
 * "place of stewardship observance". Credentialed religious office vocabulary is
 * out of bounds here, in code and in every generated page. Plain spoken register,
 * short sentences, no dashes, no codes, and no statements that promise a tax
 * outcome.

 */

/** Configuration B stays closed until the team and licensed review open it. */
export const CONFIG_B_OPEN = false;

export type HolderAnswer = 'personal' | 'structure' | 'landlord';
export type LivesThereAnswer = 'home' | 'whole' | 'no';
export type ConfigKey = 'A' | 'B' | 'C' | 'D';

export interface ChoiceOption<T extends string> {
  value: T;
  label: string;
  helper: string;
}

export const HOLDER_OPTIONS: ChoiceOption<HolderAnswer>[] = [
  {
    value: 'personal',
    label: 'I own it personally',
    helper: 'The deed is in your own name, or in the name of you and your spouse.',
  },
  {
    value: 'structure',
    label: 'The structure owns it',
    helper: 'The Association owns it, or a company the Association owns holds the title.',
  },
  {
    value: 'landlord',
    label: 'We rent it from an outside landlord',
    helper: 'Someone outside the structure owns it and rents it out.',
  },
];

export const LIVES_THERE_OPTIONS: ChoiceOption<LivesThereAnswer>[] = [
  {
    value: 'home',
    label: 'Yes, it is our home',
    helper: 'Your household lives there now, or plans to, and part of the place is used for gathering.',
  },
  {
    value: 'whole',
    label: 'Yes, the whole property serves as the center and we live in it',
    helper: 'The entire property is the center, and living there is part of how the work is carried.',
  },
  {
    value: 'no',
    label: 'No',
    helper: 'Nobody in your household lives there.',
  },
];

export function resolveConfig(holder?: HolderAnswer, lives?: LivesThereAnswer): ConfigKey | null {
  if (!holder || !lives) return null;
  if (holder === 'personal') return lives === 'whole' ? 'B' : 'A';
  if (holder === 'structure') return 'C';
  return 'D';
}

export const CONFIG_TITLES: Record<ConfigKey, string> = {
  A: 'You own it and you live there',
  B: 'The whole property is the center and you live in it',
  C: 'The structure owns the place',
  D: 'The place is rented from an outside landlord',
};

export interface RoomPortion {
  id: string;
  name: string;
  square_feet: string;
  use: string;
}

export interface CommonAreaUse {
  id: string;
  area: string;
  schedule: string;
}

export interface UtilityLine {
  id: string;
  item: string;
  who_pays: string;
  method: string;
}

export type CostMode = 'inclusive' | 'itemized';

export const COST_MODE_OPTIONS: ChoiceOption<CostMode>[] = [
  {
    value: 'inclusive',
    label: 'Simplest (recommended): the rent covers everything',
    helper: 'The entity pays one number a month and nothing else. Your household bills stay yours.',
  },
  {
    value: 'itemized',
    label: 'Itemized: the entity also pays specific costs by a written split',
    helper: 'Only pick this if something is genuinely separate, like its own meter.',
  },
];

export function costModeOf(data: HousingWorkflowData): CostMode {
  return data.cost_mode === 'itemized' ? 'itemized' : 'inclusive';
}

export interface HousingWorkflowData {
  holder?: HolderAnswer;
  lives_there?: LivesThereAnswer;
  config?: ConfigKey;

  property_address?: string;
  owner_name?: string;
  entity_legal_name?: string;
  entity_signer_name?: string;
  entity_signer_capacity?: string;
  association_officer_name?: string;
  landlord_name?: string;

  portions?: RoomPortion[];
  common_uses?: CommonAreaUse[];
  term_months?: string;
  start_date?: string;
  portion_rent?: string;

  frv_whole_property?: string;
  frv_basis?: string;
  frv_sources?: string;
  frv_portion_method?: string;

  cost_mode?: CostMode;
  utilities?: UtilityLine[];
  entity_responsibilities?: string;

  has_mortgage?: boolean;

  direction_of_payment?: boolean;
  lender_name?: string;

  mortgage_lender?: string;
  mortgage_notes?: string;

  residential_rent?: string;
  residential_portion?: string;
  self_serving_test?: string;

  gathering_cadence?: string;
  designation_notes?: string;

  log_seed?: { cadence?: string };
}

export const DIRECTION_OF_PAYMENT_TEACHING =
  'The entity can pay your lender directly for convenience. That does not change what this is. The rent is still your rental income, reported on your Schedule E, and the payment counts as rent to you applied to your loan. If you want the simplest reporting, have the entity pay you and you pay the lender.';

export const SEQUENCING_TEACHING =
  "Sign these papers before anyone moves in and before any money moves. They carry today's true date. There is no back dating here, and the portal will not offer it.";

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function configNeedsResidentialLease(data: HousingWorkflowData): boolean {
  return data.config === 'C' && (data.lives_there === 'home' || data.lives_there === 'whole');
}
